/** @jsxImportSource @emotion/react */

import React, { FC, useCallback, useMemo } from 'react'
import { useForm } from 'react-final-form'
import { toast } from 'utils/toast'
import * as classes from '../styles'
import { EndodonticTableData } from './types'
import {
  ENDODONTIC_TABLE_COLUMNS,
  ENDODONTIC_TABLE_ROWS,
  getCellKey,
  getColumnTitleKey,
  MIN_TABLE_WIDTH,
  TableRowConfig,
} from './config'

type Props = {
  name: string
  value?: string | null
}

/*
Expected data structure (flat, only non-empty values):
{
  "column_title_0": "1.5", "column_title_1": "1.6", "column_title_2": "1.7", // optional column titles
  "probing_vestibular_0_0": "6", "probing_vestibular_0_1": "7", "probing_vestibular_0_2": "9",
  "probing_vestibular_1_0": "4", "probing_vestibular_1_1": "5", "probing_vestibular_1_2": "6",
  "mobility_0_0": "1",
  ...
}
Key format for cells: "{rowKey}_{columnIndex}_{cellIndex}"
Key format for column titles: "column_title_{columnIndex}"
Only non-empty values are stored in JSON.
*/

const parseTableData = (value: string | null | undefined): EndodonticTableData => {
  if (!value) {
    return {}
  }

  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value
    // Return parsed data as-is (only non-empty values are stored)
    return parsed || {}
  } catch {
    toast.error('Invalid endodontic table data')
    console.error('Invalid endodontic table data:', value)
    return {}
  }
}

export const EndodonticTable: FC<Props> = ({ name, value }) => {
  const form = useForm()
  const tableData = useMemo(() => parseTableData(value), [value])

  const updateData = useCallback(
    (updater: (data: EndodonticTableData) => EndodonticTableData) => {
      const updated = updater(tableData)
      // Filter out empty values before saving
      const filtered: EndodonticTableData = {}
      Object.entries(updated).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          filtered[key] = value
        }
      })
      form.change(name, Object.keys(filtered).length > 0 ? JSON.stringify(filtered) : '')
    },
    [form, name, tableData]
  )

  const updateCell = useCallback(
    (rowKey: string, columnIndex: number, cellIndex: number, newValue: string) => {
      updateData((data) => {
        const key = getCellKey(rowKey, columnIndex, cellIndex)
        const updated = { ...data }

        if (newValue && newValue.trim() !== '') {
          updated[key] = newValue
        } else {
          // Remove key if value is empty
          delete updated[key]
        }

        return updated
      })
    },
    [updateData]
  )

  const updateColumnTitle = useCallback(
    (columnIndex: number, newValue: string) => {
      updateData((data) => {
        const key = getColumnTitleKey(columnIndex)
        const updated = { ...data }

        if (newValue && newValue.trim() !== '') {
          updated[key] = newValue
        } else {
          delete updated[key]
        }

        return updated
      })
    },
    [updateData]
  )

  const renderRow = (config: TableRowConfig) => {
    const rowCells: React.ReactNode[] = []

    // Group title cell (only on first row of group)
    if (config.groupTitle) {
      rowCells.push(
        <td key="groupTitle" css={classes.fixedCell} rowSpan={config.groupTitleRowSpan ?? 1}>
          {config.groupTitle}
        </td>
      )
    }

    // Row title cell
    rowCells.push(
      <td key="title" css={classes.fixedCell} colSpan={config.titleColSpan}>
        {config.title}
      </td>
    )

    // Data cells
    ENDODONTIC_TABLE_COLUMNS.forEach((columnIndex) => {
      for (let cellIndex = 0; cellIndex < config.columnsPerTooth; cellIndex++) {
        const cellKey = getCellKey(config.key, columnIndex, cellIndex)
        const cellValue = tableData[cellKey] || ''

        if (config.cellColSpan && cellIndex === 0) {
          // Single cell spanning multiple columns
          rowCells.push(
            <td key={cellKey} colSpan={config.cellColSpan}>
              <input
                type="text"
                value={cellValue}
                onChange={(e) => updateCell(config.key, columnIndex, 0, e.target.value)}
                css={classes.nativeInput}
              />
            </td>
          )
          break // Only one cell per column when colSpan is used
        } else if (!config.cellColSpan) {
          // Multiple cells per column
          rowCells.push(
            <td key={cellKey}>
              <input
                type="text"
                value={cellValue}
                onChange={(e) => updateCell(config.key, columnIndex, cellIndex, e.target.value)}
                css={classes.nativeInput}
              />
            </td>
          )
        }
      }
    })

    return <tr key={config.rowIndex}>{rowCells}</tr>
  }

  return (
    <div css={classes.tableContainer(MIN_TABLE_WIDTH)}>
      <table css={classes.table}>
        <thead>
          <tr>
            <th css={classes.fixedCell} colSpan={2}></th>
            {ENDODONTIC_TABLE_COLUMNS.map((columnIndex) => {
              const titleKey = getColumnTitleKey(columnIndex)
              const titleValue = tableData[titleKey] || ''
              return (
                <th key={columnIndex} css={classes.fixedCell} colSpan={3}>
                  <input
                    type="text"
                    value={titleValue}
                    onChange={(e) => updateColumnTitle(columnIndex, e.target.value)}
                    placeholder={`Column ${columnIndex + 1}`}
                    css={classes.nativeInput}
                  />
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>{ENDODONTIC_TABLE_ROWS.map((config) => renderRow(config))}</tbody>
      </table>
    </div>
  )
}
